'use client';

import { useState, useRef, useEffect } from 'react';

const AdvancedDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder,
  label,
  id,
  name,
  required = false,
  allowCustomInput = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [inputValue, setInputValue] = useState(value || '');
  const dropdownRef = useRef(null);

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Filter options based on input value
  useEffect(() => {
    if (inputValue) {
      const filtered = options.filter(option => 
        option.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [inputValue, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // If custom input is allowed, update the parent component
    if (allowCustomInput) {
      const event = {
        target: {
          name: name,
          value: newValue
        }
      };
      onChange(event);
    }
    
    setIsOpen(true);
  };

  const handleOptionSelect = (option) => {
    setInputValue(option);
    const event = {
      target: {
        name: name,
        value: option
      }
    };
    onChange(event);
    setIsOpen(false);
  };

  const handleInputBlur = () => {
    // If custom input is not allowed and the value is not in the options, clear it
    if (!allowCustomInput && inputValue && !options.includes(inputValue)) {
      setTimeout(() => {
        setInputValue(value || '');
      }, 200);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          id={id}
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
          required={required}
          autoComplete="off"
          onFocus={() => setIsOpen(true)}
          onBlur={handleInputBlur}
        />
        {options.length > 0 && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md max-h-60 overflow-auto text-black">
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => handleOptionSelect(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdvancedDropdown;